import React, { useMemo } from "react"
import PropTypes from "prop-types"
import { Card, Button, Typography, Space, Flex, Alert } from "antd"
import { FileOutlined, CheckCircleOutlined, CloseCircleOutlined, ReloadOutlined } from "@ant-design/icons"
import moment from "moment"
import { useTranslation } from "react-i18next"
import "./importDetail.css"
import { DSIcon } from "ui/Icon/DSIcon"
import styles from "../ViewImportRecords/viewImport.module.css"
import { useDevice } from "hooks/useDevice"
import StatusTag from "../ClientDetails/ClientDetailsTabs/BookingTab/BookingTable/StatusTag"

const { Text, Title } = Typography

const DATE_FORMAT = "MMMM DD, YYYY [at] hh:mm A"

const StatusText = ({ icon: Icon, className, text }) => (
  <Text className={className}>
    <Icon /> <Text>{text}</Text>
  </Text>
)

StatusText.propTypes = {
  icon: PropTypes.elementType.isRequired,
  className: PropTypes.string,
  text: PropTypes.node
}

const ReportCard = ({ data, onReupload }) => {
  const { t } = useTranslation()
  const { isTablet, isMobile } = useDevice()
  const { successCount, failedCount, inProgressCount } = useMemo(
    () => ({
      successCount: data?.result?.success_count || 0,
      failedCount: data?.result?.failed_count || 0,
      inProgressCount: data?.result?.in_progress || 0
    }),
    [data?.result]
  )

  const formattedDate = useMemo(() => (data?.started_at ? moment(data.started_at).format(DATE_FORMAT) : ""), [data?.started_at])

  const handleDownloadReport = (download_link) => {
    if (download_link) {
      window.open(download_link, "_blank")
    }
  }
  const isFailed = data?.current_status === "partial_completed" || data?.current_status === "skipped_failed"
  return (
    <Card className="report-card">
      <Title level={5} className="report-title mb-xxs">
        {t("clients.import_report.report")}
      </Title>
      {isTablet || isMobile ? (
        <Alert
          showIcon
          className={styles.infoAlert}
          message={t("clients.laptop_only_import_actions")}
          type="info"
          closable
          icon={<DSIcon name="info" />}
        />
      ) : null}

      <Flex align="center" gap={10} wrap justify="space-between">
        <div className="report-content">
          <Flex gap={10} className="report-summary" wrap>
            <Text>
              {t("clients.import_report.started")}: <Text>{formattedDate}</Text>
            </Text>
            <Flex gap={10}>
              {successCount > 0 && (
                <StatusText
                  icon={CheckCircleOutlined}
                  className="report-success"
                  text={t("clients.import_report.clients_imported", { count: successCount })}
                />
              )}
              {failedCount > 0 && (
                <StatusText
                  icon={CloseCircleOutlined}
                  className="report-error"
                  text={t("clients.import_report.clients_failed", { count: failedCount })}
                />
              )}
              {inProgressCount > 0 && (
                <StatusText
                  icon={ReloadOutlined}
                  className="report-in-progress"
                  text={t("clients.import_report.clients_in_progress", { count: inProgressCount })}
                />
              )}
            </Flex>
          </Flex>
          <div className="report-file-box">
            <Space>
              <FileOutlined className="file-icon" />
              <Text>{data?.filename || "N/A"}</Text>
            </Space>
            <StatusTag status={data?.current_status} statusMessage={data?.status} />
          </div>
        </div>
        {isTablet || isMobile
          ? null
          : failedCount > 0 &&
            isFailed && (
              <Flex gap={10} className="ml-auto" wrap>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={data?.failed_records_url ? () => handleDownloadReport(data?.failed_records_url) : null}
                  disabled={!data?.failed_records_url}
                >
                  {t("clients.import_report.download_error_list_count", { count: failedCount })}
                </Button>
                <Button type="primary" icon={<ReloadOutlined />} onClick={onReupload}>
                  {t("clients.import_report.reupload_fixed_errors")}
                </Button>
              </Flex>
            )}
      </Flex>
    </Card>
  )
}

export default React.memo(ReportCard)

ReportCard.propTypes = {
  data: PropTypes.shape({
    report_url: PropTypes.string,
    started_at: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    filename: PropTypes.string,
    status: PropTypes.string,
    result: PropTypes.shape({
      success_count: PropTypes.number,
      failed_count: PropTypes.number,
      in_progress: PropTypes.number
    }),
    failed_records_url: PropTypes.string,
    current_status: PropTypes.string
  }),
  onReupload: PropTypes.func
}
